import { getTranslationGroupLabel } from '../../app/terminology/components';
import { SETTINGS_SECTIONS } from '../../app/terminology/settings';
import {
    Badge,
    Banner,
    BlockStack,
    Box,
    Button,
    Card,
    Checkbox,
    Divider,
    InlineStack,
    Select,
    SkeletonBodyText,
    Tabs,
    Text,
    TextField,
} from "@shopify/polaris";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import agent from "../../app/api/agent";
import type { StorefrontLanguage, StorefrontTranslationEntry, StorefrontTranslations } from "../../app/models/StorefrontTranslations";
import { SettingsCard } from "./SettingsControls";
import "./StorefrontTranslationsSettings.css";

export type StorefrontTranslationsEditorStatus = {
    text?: Record<string, string>;
    locale?: string;
    error?: string;
    reload?: () => void;
    hasChanges: boolean;
    loading: boolean;
    saving: boolean;
    save?: () => Promise<void>;
    discard?: () => void;
};

type StorefrontTranslationsSettingsProps = {
    onStatusChange?: (status: StorefrontTranslationsEditorStatus) => void;
    onHeaderActionsChange?: (content: ReactNode | null) => void;
};

const snapshot = (values: Record<string, string>) => JSON.stringify(
    Object.keys(values)
        .sort((left, right) => left.localeCompare(right))
        .reduce<Record<string, string>>((accumulator, key) => {
            accumulator[key] = values[key] || "";
            return accumulator;
        }, {})
);

const entryValues = (entries: StorefrontTranslationEntry[]) =>
    entries.reduce<Record<string, string>>((accumulator, entry) => {
        accumulator[entry.key] = entry.value || "";
        return accumulator;
    }, {});

const languageLabel = (language: StorefrontLanguage) => {
    const parts = [language.name || language.locale.toUpperCase()];
    if (language.primary) parts.push("Default");
    if (!language.published) parts.push("Unpublished");
    return `${parts.join(" - ")} (${language.locale})`;
};

const groupEntries = (entries: StorefrontTranslationEntry[]) =>
    entries.reduce<Record<string, StorefrontTranslationEntry[]>>((groups, entry) => {
        groups[entry.group] = groups[entry.group] || [];
        groups[entry.group].push(entry);
        return groups;
    }, {});

const groupOrder = [
    "Vehicle Search",
    "Selector fields",
    "Product Results",
    "Fit Check",
    "Fitment Table",
    "Product Guide",
];

const selectorFieldValueOrder: Record<string, number> = {
    display_label: 1,
    placeholder: 2,
    empty_option: 3,
};

const selectorFieldFallbackId = (entry: StorefrontTranslationEntry) =>
    entry.entityId || entry.entityKey || entry.entityLabel || entry.key;

const sortTranslationGroups = (groups: [string, StorefrontTranslationEntry[]][]) =>
    groups.sort(([left], [right]) => {
        const leftIndex = groupOrder.indexOf(left);
        const rightIndex = groupOrder.indexOf(right);
        if (leftIndex !== -1 || rightIndex !== -1) {
            return (leftIndex === -1 ? Number.MAX_SAFE_INTEGER : leftIndex)
                - (rightIndex === -1 ? Number.MAX_SAFE_INTEGER : rightIndex);
        }

        return left.localeCompare(right);
    });

const groupSelectorFieldEntries = (entries: StorefrontTranslationEntry[]) => {
    const groups = entries.reduce<Record<string, StorefrontTranslationEntry[]>>((accumulator, entry) => {
        const id = selectorFieldFallbackId(entry);
        accumulator[id] = accumulator[id] || [];
        accumulator[id].push(entry);
        return accumulator;
    }, {});

    return Object.entries(groups)
        .map(([id, fieldEntries]) => ({
            id,
            label: fieldEntries[0]?.entityLabel || fieldEntries[0]?.entityKey || "Selector field",
            displayOrder: fieldEntries[0]?.displayOrder ?? Number.MAX_SAFE_INTEGER,
            entries: fieldEntries.slice().sort((left, right) =>
                (selectorFieldValueOrder[left.valueType || ""] ?? 99)
                - (selectorFieldValueOrder[right.valueType || ""] ?? 99)
            ),
        }))
        .sort((left, right) => (left.displayOrder - right.displayOrder) || left.label.localeCompare(right.label));
};

// Storefront copy editor. Blank fields intentionally mean "use the built-in pack or English default".
const StorefrontTranslationsSettings = ({ onStatusChange, onHeaderActionsChange }: StorefrontTranslationsSettingsProps) => {
    const [data, setData] = useState<StorefrontTranslations>();
    const [previewData, setPreviewData] = useState<StorefrontTranslations>();
    const [selectedLocale, setSelectedLocale] = useState("");
    const [selectedPreset, setSelectedPreset] = useState("");
    const [overwritePresetText, setOverwritePresetText] = useState(false);
    const [presetDialogOpen, setPresetDialogOpen] = useState(false);
    const [presetError, setPresetError] = useState<string>();
    const presetDialogRef = useRef<HTMLDialogElement>(null);
    const [draft, setDraft] = useState<Record<string, string>>({});
    const [baseline, setBaseline] = useState<Record<string, string>>({});
    const [selectedGroupTab, setSelectedGroupTab] = useState(0);
    const [searchQuery, setSearchQuery] = useState("");
    const [focusedEntryKey, setFocusedEntryKey] = useState<string>();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [applyingPreset, setApplyingPreset] = useState(false);
    const [previewingPreset, setPreviewingPreset] = useState(false);
    const [error, setError] = useState<string>();
    const [success, setSuccess] = useState<string>();
    const presetPreviewRequestId = useRef(0);

    const loadTranslations = useCallback(async (locale?: string) => {
        // The backend returns Shopify languages plus all editable label definitions for this locale.
        presetPreviewRequestId.current += 1;
        setLoading(true);
        setPreviewingPreset(false);
        setPreviewData(undefined);
        setError(undefined);
        setSuccess(undefined);

        try {
            const response = await agent.Settings.storefrontTranslations(locale);
            const values = entryValues(response.entries);
            setData(response);
            setPreviewData(undefined);
            setSelectedLocale(response.selectedLocale);
            setSelectedPreset(response.selectedTerminologyPreset);
            setOverwritePresetText(false);
            setDraft(values);
            setBaseline(values);
        } catch (err: any) {
            setError(err?.message ?? "Failed to load translations.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadTranslations();
    }, [loadTranslations]);

    const hasChanges = snapshot(draft) !== snapshot(baseline);
    const presetHasChanges = !!data && selectedPreset !== data.selectedTerminologyPreset;
    const displayEntries = useMemo(
        () => previewData?.entries ?? data?.entries ?? [],
        [data?.entries, previewData?.entries]
    );
    const groupedEntries = useMemo(() => groupEntries(displayEntries), [displayEntries]);
    const groups = useMemo(() => sortTranslationGroups(Object.entries(groupedEntries)), [groupedEntries]);

    const languageOptions = useMemo(
        () => (data?.languages ?? []).map((language) => ({
            label: languageLabel(language),
            value: language.locale,
        })),
        [data?.languages]
    );
    const presetOptions = useMemo(
        () => (data?.terminologyPresets ?? []).map((preset) => ({
            label: preset.label,
            value: preset.value,
        })),
        [data?.terminologyPresets]
    );

    const selectedLanguage = useMemo(
        () => (data?.languages ?? []).find((language) => language.locale === selectedLocale),
        [data?.languages, selectedLocale]
    );

    const appliedPresetLabel = presetOptions.find(option => option.value === data?.selectedTerminologyPreset)?.label;
    const openPresetDialog = useCallback(() => {
        setSelectedPreset(data?.selectedTerminologyPreset ?? "");
        setOverwritePresetText(false);
        setPresetError(undefined);
        setPresetDialogOpen(true);
    }, [data?.selectedTerminologyPreset]);
    const closePresetDialog = useCallback(() => {
        if (applyingPreset) return;
        presetPreviewRequestId.current += 1;
        setPreviewData(undefined);
        setPreviewingPreset(false);
        setSelectedPreset(data?.selectedTerminologyPreset ?? "");
        setOverwritePresetText(false);
        setPresetError(undefined);
        setPresetDialogOpen(false);
    }, [applyingPreset, data?.selectedTerminologyPreset]);

    useEffect(() => {
        const dialog = presetDialogRef.current;
        if (!presetDialogOpen || !dialog) return;
        const opener = dialog.ownerDocument.activeElement as HTMLElement | null;
        // Stay in the owning App Bridge modal document, as the selector field dialog does.
        dialog.showModal();
        dialog.querySelector<HTMLSelectElement>("select")?.focus();
        return () => {
            dialog.close();
            if (opener?.isConnected) opener.focus();
        };
    }, [presetDialogOpen]);

    useEffect(() => {
        if (!presetDialogOpen || !data || !selectedLocale || !selectedPreset) {
            presetPreviewRequestId.current += 1;
            setPreviewData(undefined);
            setPreviewingPreset(false);
            return;
        }

        if (selectedPreset === data.selectedTerminologyPreset && !overwritePresetText) {
            presetPreviewRequestId.current += 1;
            setPreviewData(undefined);
            setPreviewingPreset(false);
            return;
        }

        const requestId = presetPreviewRequestId.current + 1;
        presetPreviewRequestId.current = requestId;
        setPreviewingPreset(true);
        setPresetError(undefined);

        agent.Settings.previewStorefrontTerminologyPreset({
            locale: selectedLocale,
            preset: selectedPreset,
            overwriteCustomizedText: overwritePresetText,
        })
            .then((response) => {
                if (presetPreviewRequestId.current !== requestId) return;
                setPreviewData(response);
                setPresetError(undefined);
            })
            .catch((err: any) => {
                if (presetPreviewRequestId.current !== requestId) return;
                setPreviewData(undefined);
                setPresetError(err?.message ?? "Failed to preview terminology preset.");
            })
            .finally(() => {
                if (presetPreviewRequestId.current === requestId) {
                    setPreviewingPreset(false);
                }
            });
    }, [data, overwritePresetText, presetDialogOpen, selectedLocale, selectedPreset]);

    const handleLocaleChange = useCallback((locale: string) => {
        setSelectedLocale(locale);
        loadTranslations(locale);
    }, [loadTranslations]);

    useEffect(() => {
        setSelectedGroupTab((current) => Math.min(current, Math.max(groups.length - 1, 0)));
    }, [groups.length]);

    const updateDraftValue = useCallback((key: string, value: string) => {
        setSuccess(undefined);
        setDraft((current) => ({ ...current, [key]: value }));
    }, []);

    const displayValueForEntry = useCallback((entry: StorefrontTranslationEntry) => {
        if (previewData && !hasChanges) {
            return entry.value || "";
        }

        return draft[entry.key] ?? "";
    }, [draft, hasChanges, previewData]);

    const normalizedQuery = searchQuery.trim().toLocaleLowerCase();
    const matchingEntries = displayEntries.filter((entry) => {
        const value = displayValueForEntry(entry);
        return [entry.label, entry.entityLabel, value.trim() || entry.builtInValue || entry.defaultValue]
            .some((text) => text?.toLocaleLowerCase().includes(normalizedQuery));
    });
    // Keep a matching field mounted while its text is being edited, even if the new value no longer matches.
    const visibleGroups = normalizedQuery
        ? sortTranslationGroups(Object.entries(groupEntries(displayEntries.filter((entry) =>
            entry.key === focusedEntryKey || matchingEntries.includes(entry)
        ))))
        : groups.slice(selectedGroupTab, selectedGroupTab + 1);

    const renderEntry = (entry: StorefrontTranslationEntry) => (
        <TextField
            key={entry.key}
            label={entry.label}
            autoComplete="off"
            value={displayValueForEntry(entry)}
            placeholder={entry.builtInValue || entry.defaultValue || "Blank"}
            multiline={entry.multiline ? 3 : false}
            onChange={(value) => updateDraftValue(entry.key, value)}
            onFocus={() => setFocusedEntryKey(entry.key)}
            onBlur={() => setFocusedEntryKey(undefined)}
            disabled={loading || saving || applyingPreset}
        />
    );

    const discardChanges = useCallback(() => {
        setDraft(baseline);
        setSuccess(undefined);
    }, [baseline]);

    const saveTranslations = useCallback(async () => {
        if (!selectedLocale) return;

        // Blank values are sent too; the API interprets them as delete override.
        presetPreviewRequestId.current += 1;
        setSaving(true);
        setPreviewingPreset(false);
        setPreviewData(undefined);
        setError(undefined);
        setSuccess(undefined);

        try {
            const response = await agent.Settings.updateStorefrontTranslations(selectedLocale, {
                translations: draft,
            });
            const values = entryValues(response.entries);
            setData(response);
            setPreviewData(undefined);
            setSelectedPreset(response.selectedTerminologyPreset);
            setDraft(values);
            setBaseline(values);
            setSuccess("Translations saved.");
        } catch (err: any) {
            setError(err?.message ?? "Failed to save translations.");
        } finally {
            setSaving(false);
        }
    }, [draft, selectedLocale]);

    const applyTerminologyPreset = useCallback(async () => {
        if (!selectedLocale || !selectedPreset || hasChanges) return;

        presetPreviewRequestId.current += 1;
        setApplyingPreset(true);
        setPreviewingPreset(false);
        setPreviewData(undefined);
        setError(undefined);
        setPresetError(undefined);
        setSuccess(undefined);

        try {
            const response = await agent.Settings.updateStorefrontTerminologyPreset({
                locale: selectedLocale,
                preset: selectedPreset,
                overwriteCustomizedText: overwritePresetText,
            });
            const values = entryValues(response.entries);
            setData(response);
            setPreviewData(undefined);
            setSelectedPreset(response.selectedTerminologyPreset);
            setOverwritePresetText(false);
            setDraft(values);
            setBaseline(values);
            setSuccess("Terminology preset applied.");
            setPresetDialogOpen(false);
        } catch (err: any) {
            setPresetError(err?.message ?? "Failed to apply terminology preset.");
        } finally {
            setApplyingPreset(false);
        }
    }, [hasChanges, overwritePresetText, selectedLocale, selectedPreset]);

    useEffect(() => {
        onStatusChange?.({
            text: data ? Object.fromEntries(data.entries.flatMap(entry => {
                const value = draft[entry.key]?.trim();
                // Untranslated selector defaults follow the field draft, including renames.
                if (entry.entityType === "SelectorField" && !value && entry.builtInValue === entry.defaultValue) return [];
                return [[entry.key, value || entry.builtInValue || entry.defaultValue]];
            })) : undefined,
            locale: data?.selectedLocale,
            error,
            reload: !data ? () => { void loadTranslations(); } : undefined,
            hasChanges,
            loading,
            saving: saving || applyingPreset,
            save: saveTranslations,
            discard: discardChanges,
        });
    }, [data, draft, error, loadTranslations, applyingPreset, discardChanges, hasChanges, loading, onStatusChange, saveTranslations, saving]);

    const showLocalActions = !onStatusChange;

    const languageControls = useMemo(() => (
        <InlineStack gap="200" blockAlign="end" align="end">
            <Button variant="tertiary" onClick={openPresetDialog} disabled={loading || saving || applyingPreset}>
                {appliedPresetLabel ? `Terminology: ${appliedPresetLabel}` : "Terminology preset"}
            </Button>
            <div className="pf-storefront-text__language-select">
                <Select
                    label="Language"
                    labelHidden
                    options={languageOptions}
                    value={selectedLocale}
                    onChange={handleLocaleChange}
                    disabled={loading || saving || applyingPreset}
                />
            </div>
            {selectedLanguage && !selectedLanguage.published ? <Badge>Unpublished</Badge> : null}
            {showLocalActions ? (
                <>
                    <Button onClick={discardChanges} disabled={!hasChanges || saving || applyingPreset}>
                        Discard
                    </Button>
                    <Button variant="primary" onClick={saveTranslations} loading={saving} disabled={!hasChanges || loading || applyingPreset}>
                        Save
                    </Button>
                </>
            ) : null}
        </InlineStack>
    ), [
        appliedPresetLabel,
        applyingPreset,
        discardChanges,
        handleLocaleChange,
        hasChanges,
        languageOptions,
        loading,
        openPresetDialog,
        saveTranslations,
        saving,
        selectedLanguage,
        selectedLocale,
        showLocalActions,
    ]);

    const headerControls = data ? languageControls : null;
    const canApplyPreset = !!selectedLocale
        && !!selectedPreset
        && !hasChanges
        && !loading
        && !saving
        && !applyingPreset
        && !previewingPreset
        && (presetHasChanges || overwritePresetText);

    useEffect(() => {
        if (!onHeaderActionsChange) return;
        onHeaderActionsChange(headerControls);
        return () => onHeaderActionsChange(null);
    }, [headerControls, onHeaderActionsChange]);

    if (loading && !data) {
        return (
            <SettingsCard title={SETTINGS_SECTIONS.storefrontText}>
                <SkeletonBodyText lines={8} />
            </SettingsCard>
        );
    }

    return (
        <BlockStack gap="300">
            {!onHeaderActionsChange && headerControls ? (
                <InlineStack align="end" blockAlign="center">
                    {headerControls}
                </InlineStack>
            ) : null}

            {data?.warning ? (
                <Banner tone="warning">
                    <Text as="p">{data.warning}</Text>
                </Banner>
            ) : null}

            {error ? (
                <Banner tone="critical">
                    <Text as="p">{error}</Text>
                </Banner>
            ) : null}

            {success ? (
                <Banner tone="success">
                    <Text as="p">{success}</Text>
                </Banner>
            ) : null}

            {data && presetDialogOpen ? (
                <dialog
                    ref={presetDialogRef}
                    className="pf-storefront-text__preset-dialog"
                    aria-label="Terminology preset"
                    aria-modal="true"
                    onCancel={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        closePresetDialog();
                    }}
                >
                    <BlockStack gap="400">
                        <BlockStack gap="200">
                            <InlineStack gap="200" blockAlign="center">
                                <Text as="h2" variant="headingMd">
                                    Terminology preset
                                </Text>
                                <Badge>Optional</Badge>
                            </InlineStack>
                            <Text as="p" variant="bodySm" tone="subdued">
                                Presets are prepared sets of storefront text, such as wording for vehicles or equipment. You can use one as a starting point, or keep and edit your existing text without applying a preset.
                            </Text>
                            <Text as="p" variant="bodySm" tone="subdued">
                                Applies to {selectedLanguage?.name || selectedLocale}. Your customized text stays in place unless you choose to replace it.
                            </Text>
                        </BlockStack>
                        {presetError ? <Banner tone="critical"><Text as="p">{presetError}</Text></Banner> : null}
                        <Select
                            label="Terminology preset"
                            options={presetOptions}
                            value={selectedPreset}
                            onChange={(value) => {
                                setSuccess(undefined);
                                setSelectedPreset(value);
                            }}
                            disabled={loading || saving || applyingPreset}
                        />
                        <Checkbox
                            label="Replace customized text for this language"
                            checked={overwritePresetText}
                            onChange={(checked) => {
                                setSuccess(undefined);
                                setOverwritePresetText(checked);
                            }}
                            disabled={loading || saving || applyingPreset}
                        />
                        {hasChanges ? (
                            <Text as="p" variant="bodySm" tone="subdued">
                                Save or discard text changes before applying a preset.
                            </Text>
                        ) : null}
                        <InlineStack gap="200" align="end">
                            <Button onClick={closePresetDialog} disabled={applyingPreset}>Cancel</Button>
                            <Button variant="primary" onClick={applyTerminologyPreset} loading={applyingPreset} disabled={!canApplyPreset}>
                                Apply preset
                            </Button>
                        </InlineStack>
                    </BlockStack>
                </dialog>
            ) : null}

            <Card padding="0">
                <Box paddingBlockStart="400" paddingBlockEnd="300" paddingInline="600">
                    <TextField
                        label="Search storefront text"
                        labelHidden
                        type="search"
                        placeholder="Search storefront text…"
                        autoComplete="off"
                        value={searchQuery}
                        onChange={(value) => {
                            setFocusedEntryKey(undefined);
                            setSearchQuery(value);
                        }}
                        clearButton
                        onClearButtonClick={() => {
                            setFocusedEntryKey(undefined);
                            setSearchQuery("");
                        }}
                        helpText="Search labels and text across all sections in the selected language."
                    />
                </Box>
                {normalizedQuery ? (
                    <Box paddingBlockEnd="300" paddingInline="600">
                        <div role="status">
                            <Text as="p" tone="subdued">
                                {matchingEntries.length} {matchingEntries.length === 1 ? "result" : "results"}
                            </Text>
                        </div>
                    </Box>
                ) : (
                    <Box paddingBlockStart="400" paddingInline="600">
                        <Tabs
                            tabs={groups.map(([group]) => ({
                                id: `${group.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-translations-tab`,
                                content: getTranslationGroupLabel(group),
                                panelID: `${group.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-translations-panel`,
                            }))}
                            selected={selectedGroupTab}
                            onSelect={setSelectedGroupTab}
                        />
                    </Box>
                )}
                <Divider />
                <Box paddingBlockStart="500" paddingBlockEnd="500" paddingInline="600">
                    <BlockStack gap="600">
                        {visibleGroups.map(([group, entries]) => (
                            <BlockStack gap="400" key={group}>
                                <Text as="h2" variant="headingMd">{getTranslationGroupLabel(group)}</Text>
                                {group === "Selector fields" ? (
                                    <BlockStack gap="500">
                                        {groupSelectorFieldEntries(entries).map((fieldGroup, index, fieldGroups) => (
                                            <BlockStack gap="400" key={fieldGroup.id}>
                                                <BlockStack gap="300">
                                                    <Text as="h3" variant="headingSm">
                                                        {fieldGroup.label}
                                                    </Text>
                                                    {fieldGroup.entries.map(renderEntry)}
                                                </BlockStack>
                                                {index < fieldGroups.length - 1 ? <Divider /> : null}
                                            </BlockStack>
                                        ))}
                                    </BlockStack>
                                ) : entries.map(renderEntry)}
                            </BlockStack>
                        ))}
                        {visibleGroups.length === 0 ? (
                            <Text as="p" tone="subdued">
                                {normalizedQuery ? "No matching text. Try another search or clear the search to view all sections." : "No translation labels found."}
                            </Text>
                        ) : null}
                    </BlockStack>
                </Box>
            </Card>
        </BlockStack>
    );
};

export default StorefrontTranslationsSettings;
